import React, { useState } from 'react';
import { useGetProfileQuery, useUpdateProfileMutation } from '../redux/services/authApi';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  FormControl,
  FormHelperText
} from '@mui/material';

const Profile = () => {
  const { data: profile, isLoading, error } = useGetProfileQuery();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [formData, setFormData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const { showToast } = useToast();

  React.useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        password: '',
      });
    }
  }, [profile]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      // Only include non-empty values in the request
      const formDataToSend = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value)
      );
      
      await updateProfile(formDataToSend).unwrap();
      showToast('Profile updated successfully!', 'success');
    } catch (err) {
      setFormErrors(err.data?.errors || {});
      showToast('Failed to update profile. Please try again.', 'error');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading profile
      </Alert>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
        p: 3,
        borderRadius: 2,
      }}
    >
      <Typography variant="h4" component="h2" gutterBottom>
        Edit Profile
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.last_name}>
              <TextField
                label="Last Name"
                name="last_name"
                value={formData?.last_name || ''}
                onChange={handleInputChange}
                disabled={isUpdating}
                variant="outlined"
                fullWidth
                error={!!formErrors.last_name}
              />
              {formErrors.last_name && (
                <FormHelperText>{formErrors.last_name}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.first_name}>
              <TextField
                label="First Name"
                name="first_name"
                value={formData?.first_name || ''}
                onChange={handleInputChange}
                disabled={isUpdating}
                variant="outlined"
                fullWidth
                error={!!formErrors.first_name}
              />
              {formErrors.first_name && (
                <FormHelperText>{formErrors.first_name}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.email}>
              <TextField
                label="Email"
                type="email"
                name="email"
                value={formData?.email || ''}
                onChange={handleInputChange}
                disabled={true}
                variant="outlined"
                fullWidth
                error={!!formErrors.email}
              />
              {formErrors.email && (
                <FormHelperText>{formErrors.email}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.phone}>
              <TextField
                label="Phone"
                name="phone"
                value={formData?.phone || ''}
                onChange={handleInputChange}
                disabled={isUpdating}
                variant="outlined"
                fullWidth
                error={!!formErrors.phone}
              />
              {formErrors.phone && (
                <FormHelperText>{formErrors.phone}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.password}>
              <TextField
                label="Password"
                type="password"
                name="password"
                autoComplete="new-password"
                value={formData?.password || ''}
                placeholder="Enter new password if you want to change it"
                onChange={handleInputChange}
                disabled={isUpdating}
                variant="outlined"
                fullWidth
                error={!!formErrors.password}
                helperText={formErrors.password || "Leave blank to keep current password"}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              disabled={isUpdating}
              sx={{ mt: 2, py: 1.5 }}
              variant="contained"
              color="primary"
              fullWidth
            >
              {isUpdating ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default Profile;